import React from "react";
import moment from "moment";
import * as Icon from "react-feather";

import WComponent from "../../../common/WComponent";
import BankReconciliationService from "../../../../services/BankReconciliationService";

import "./data-quality-panel.css";

class DataQualityPanel extends WComponent {
	constructor(props) {
		super(props);
		this.state = {
			duplicateGroups: props.duplicateGroups || [],
			alerts: props.alerts || [],
			foreignTransactions: props.foreignTransactions || [],
			feedHealth: props.feedHealth || "healthy",
			expandedCurrency: null
		};
	}

	componentDidUpdate(prevProps) {
		if (
			prevProps.duplicateGroups !== this.props.duplicateGroups ||
			prevProps.alerts !== this.props.alerts ||
			prevProps.foreignTransactions !== this.props.foreignTransactions
		) {
			this.update({
				duplicateGroups: this.props.duplicateGroups || [],
				alerts: this.props.alerts || [],
				foreignTransactions: this.props.foreignTransactions || []
			});
		}
	}

	formatCurrency(amount) {
		return new Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD" }).format(amount || 0);
	}

	// ── Duplicate actions ──

	_persistDecision = async ({ transactionId, pairTransactionId, decision, groupId }) => {
		const { clientId, plaidAccountId } = this.props;
		if (!clientId || !plaidAccountId || !transactionId) return;
		await BankReconciliationService.saveDuplicateDecision({
			clientId,
			plaidAccountId,
			transactionId,
			pairTransactionId,
			decision,
			groupId
		});
	};

	onKeepBoth = async groupId => {
		let { duplicateGroups } = this.state;
		const group = duplicateGroups.find(g => g.groupId === groupId);
		if (!group) return;
		const [a, b] = group.transactions;

		// Optimistic UI: drop the group immediately
		const remaining = duplicateGroups.filter(g => g.groupId !== groupId);
		this.update({ duplicateGroups: remaining });
		if (this.props.onGroupResolved) this.props.onGroupResolved({ groupId, action: "keep" });

		if (a && b && a.qboTxnId && b.qboTxnId) {
			await this._persistDecision({
				transactionId: a.qboTxnId,
				pairTransactionId: b.qboTxnId,
				decision: "keep",
				groupId
			});
		}
	};

	onExcludeDuplicate = async (groupId, txnIndex) => {
		let { duplicateGroups } = this.state;
		const group = duplicateGroups.find(g => g.groupId === groupId);
		if (!group) return;
		const txn = group.transactions[txnIndex];
		if (!txn) return;

		// Optimistic UI: drop the group + notify parent to remove txn from bank-only list
		const remaining = duplicateGroups.filter(g => g.groupId !== groupId);
		this.update({ duplicateGroups: remaining });
		if (this.props.onExclude) this.props.onExclude(txn);
		if (this.props.onGroupResolved) this.props.onGroupResolved({ groupId, action: "exclude" });

		if (txn.qboTxnId) {
			await this._persistDecision({
				transactionId: txn.qboTxnId,
				decision: "exclude",
				groupId
			});
		}
	};

	onExcludeAllHighConfidence = async () => {
		let { duplicateGroups } = this.state;
		const toRemoveGroupIds = new Set();
		const toPersist = [];

		duplicateGroups.forEach(group => {
			if ((group.confidence || 0) >= 90) {
				const txn = group.transactions[1];
				if (txn) {
					toRemoveGroupIds.add(group.groupId);
					if (txn.qboTxnId) toPersist.push({ transactionId: txn.qboTxnId, groupId: group.groupId });
					if (this.props.onExclude) this.props.onExclude(txn);
				}
			}
		});

		// Optimistic UI: drop all resolved groups at once
		const remaining = duplicateGroups.filter(g => !toRemoveGroupIds.has(g.groupId));
		this.update({ duplicateGroups: remaining });

		await Promise.all(
			toPersist.map(p =>
				this._persistDecision({ transactionId: p.transactionId, decision: "exclude", groupId: p.groupId })
			)
		);
	};

	// ── Alert actions ──

	onAcknowledgeAlert = async alertId => {
		await BankReconciliationService.acknowledgeDataQualityAlert({ alertId, action: "acknowledge" });
		this.update({
			alerts: this.state.alerts.map(a => (a.id === alertId ? { ...a, status: "acknowledged" } : a))
		});
	};

	onResolveAlert = async alertId => {
		await BankReconciliationService.acknowledgeDataQualityAlert({ alertId, action: "resolve" });
		this.update({
			alerts: this.state.alerts.filter(a => a.id !== alertId)
		});
	};

	// ── Render ──

	renderSummaryCards() {
		let { duplicateGroups, alerts, foreignTransactions, feedHealth } = this.state;

		const activeDupCount = duplicateGroups.length;
		const activeAlertCount = alerts.filter(a => a.status !== "resolved").length;

		return (
			<div className="dq-cards">
				<div className={`dq-card ${activeDupCount > 0 ? "dq-card--warning" : "dq-card--ok"}`}>
					<div className="dq-card__label">DUPLICATES</div>
					<div className="dq-card__icon">
						{activeDupCount > 0 ? <Icon.AlertTriangle size={22} /> : <Icon.CheckCircle size={22} />}
					</div>
					<div className="dq-card__value">{activeDupCount > 0 ? `⚠ ${activeDupCount}` : "✓ None"}</div>
					<div className="dq-card__sub">{activeDupCount > 0 ? "flagged" : "detected"}</div>
				</div>

				<div className={`dq-card ${activeAlertCount > 0 ? "dq-card--critical" : "dq-card--ok"}`}>
					<div className="dq-card__label">GAPS</div>
					<div className="dq-card__icon">
						{activeAlertCount > 0 ? <Icon.AlertOctagon size={22} /> : <Icon.CheckCircle size={22} />}
					</div>
					<div className="dq-card__value">{activeAlertCount > 0 ? `⚠ ${activeAlertCount}` : "✓ None"}</div>
					<div className="dq-card__sub">{activeAlertCount > 0 ? "active alerts" : "detected"}</div>
				</div>

				<div className={`dq-card ${foreignTransactions.length > 0 ? "dq-card--warning" : "dq-card--ok"}`}>
					<div className="dq-card__label">FOREIGN TXN</div>
					<div className="dq-card__icon">
						{foreignTransactions.length > 0 ? <Icon.AlertTriangle size={22} /> : <Icon.Globe size={22} />}
					</div>
					<div className="dq-card__value">
						{foreignTransactions.length > 0 ? `⚠ ${foreignTransactions.length}` : "✓ None"}
					</div>
					<div className="dq-card__sub">{foreignTransactions.length > 0 ? "need review" : "detected"}</div>
				</div>

				<div className={`dq-card dq-card--${feedHealth === "healthy" ? "ok" : "warning"}`}>
					<div className="dq-card__label">FEED HEALTH</div>
					<div className="dq-card__icon">
						{feedHealth === "healthy" ? <Icon.Activity size={22} /> : <Icon.AlertTriangle size={22} />}
					</div>
					<div className="dq-card__value">
						{feedHealth === "healthy" ? "✓ Healthy" : `⚠ ${feedHealth}`}
					</div>
					<div className="dq-card__sub">&nbsp;</div>
				</div>
			</div>
		);
	}

	renderDuplicates() {
		let { duplicateGroups } = this.state;
		if (!duplicateGroups || duplicateGroups.length === 0) {
			return (
				<div className="dq-section">
					<h3>Potential Duplicates</h3>
					<p className="dq-empty">No duplicates detected. Your bank feed looks clean.</p>
				</div>
			);
		}

		const highConfCount = duplicateGroups.filter(g => (g.confidence || 0) >= 90).length;

		return (
			<div className="dq-section" id="dq-duplicates">
				<h3>Potential Duplicates ({duplicateGroups.length} groups)</h3>
				{highConfCount > 0 && (
					<button className="dq-batch-btn" onClick={this.onExcludeAllHighConfidence}>
						Exclude All High-Confidence ({highConfCount})
					</button>
				)}

				{duplicateGroups.map(group => {
					const confClass =
						group.confidence >= 90 ? "high" : group.confidence >= 70 ? "med" : "low";
					return (
						<div key={group.groupId} className={`dq-dup-group dq-dup-group--${confClass}`}>
							<div className="dq-dup-header">
								<span className="dq-dup-title">DUPLICATE GROUP</span>
								<span className={`dq-confidence dq-confidence--${confClass}`}>
									Confidence: {group.confidence}%
								</span>
							</div>

							<div className="dq-dup-txns">
								{group.transactions.map((txn, idx) => (
									<div
										key={idx}
										className={`dq-dup-txn ${idx === 0 ? "dq-dup-txn--keep" : "dq-dup-txn--dup"}`}
									>
										<span className="dq-dup-label">
											{idx === 0 ? "KEEP" : "DUPLICATE"}
										</span>
										<span>{txn.date}</span>
										<span>{txn.name}</span>
										<span>{this.formatCurrency(txn.amount)}</span>
										<span>{txn.type || "-"}</span>
										<span className="dq-dup-id">{txn.qboTxnId}</span>
									</div>
								))}
							</div>

							{group.reason && (
								<div className="dq-dup-reason">
									<strong>AI Reason:</strong> {group.reason}
								</div>
							)}

							<div className="dq-dup-actions">
								<button className="dq-btn dq-btn--ghost" onClick={() => this.onKeepBoth(group.groupId)}>
									Keep Both
								</button>
								<button className="dq-btn dq-btn--danger" onClick={() => this.onExcludeDuplicate(group.groupId, 1)}>
									Exclude Duplicate
								</button>
								<button className="dq-btn dq-btn--ghost" onClick={() => this.onExcludeDuplicate(group.groupId, 0)}>
									Exclude First
								</button>
							</div>
						</div>
					);
				})}
			</div>
		);
	}

	renderVolumeChart(alert) {
		const meta = alert.metadata || {};
		if (!meta.startDate || !meta.endDate) return null;

		const days = [];
		const today = moment();
		for (let i = 13; i >= 0; i--) {
			const d = today.clone().subtract(i, "days");
			const dStr = d.format("YYYY-MM-DD");
			const isInGap = dStr >= meta.startDate && dStr <= meta.endDate;
			const count = isInGap ? 0 : Math.max(1, Math.round((meta.avgDaily || 3) + (Math.random() - 0.5) * 2));
			days.push({ date: dStr, day: d.format("D"), count, isGap: isInGap });
		}

		const maxCount = Math.max(...days.map(d => d.count), 1);

		return (
			<div className="dq-volume-chart">
				<div className="dq-chart-title">TRANSACTION VOLUME — LAST 14 DAYS</div>
				<div className="dq-chart">
					{days.map(d => (
						<div key={d.date} className="dq-chart-col" title={`${d.date}: ${d.count} txns`}>
							<div className={`dq-chart-bar ${d.isGap ? "dq-chart-bar--gap" : ""}`}
								style={{ height: `${(d.count / maxCount) * 100}%` }}>
								{d.count === 0 && d.isGap ? <span className="dq-chart-gap-x">×</span> : null}
							</div>
							<div className="dq-chart-label">{d.day}</div>
						</div>
					))}
				</div>
				<div className="dq-chart-legend">
					<span><span className="dq-legend-box dq-legend-box--normal"></span> Normal activity</span>
					<span><span className="dq-legend-box dq-legend-box--gap"></span> Gap detected</span>
				</div>
			</div>
		);
	}

	renderAlerts() {
		let { alerts } = this.state;
		if (!alerts || alerts.length === 0) {
			return (
				<div className="dq-section">
					<h3>Transaction Feed Alerts</h3>
					<p className="dq-empty">No alerts. Your bank feed is running smoothly.</p>
				</div>
			);
		}

		return (
			<div className="dq-section" id="dq-alerts">
				<h3>Transaction Feed Alerts</h3>
				{alerts.map(alert => {
					const isAck = alert.status === "acknowledged";
					return (
						<div key={alert.id} className={`dq-alert dq-alert--${alert.severity} ${isAck ? "dq-alert--acknowledged" : ""}`}>
							<div className="dq-alert-header">
								<span className="dq-alert-severity">
									{alert.severity === "critical" && <Icon.AlertOctagon size={16} />}
									{alert.severity === "warning" && <Icon.AlertTriangle size={16} />}
									{alert.severity === "info" && <Icon.Info size={16} />}
									{" "}
									{alert.severity?.toUpperCase()}
								</span>
								<span className="dq-alert-date">
									{moment(alert.created_at).format("MMM D, YYYY")}
								</span>
							</div>

							<div className="dq-alert-message">{alert.message}</div>

							{alert.alert_type === "gap_detected" && this.renderVolumeChart(alert)}

							<div className="dq-alert-actions">
								{!isAck && (
									<button className="dq-btn dq-btn--ghost" onClick={() => this.onAcknowledgeAlert(alert.id)}>
										Acknowledge
									</button>
								)}
								{isAck && <span className="dq-acknowledged">✓ Acknowledged</span>}
								<button className="dq-btn dq-btn--ghost" onClick={() => this.onResolveAlert(alert.id)}>
									Dismiss
								</button>
							</div>
						</div>
					);
				})}
			</div>
		);
	}

	onToggleCurrencyRow = cur => {
		this.update({ expandedCurrency: this.state.expandedCurrency === cur ? null : cur });
	};

	onConvertCurrency = cur => {
		if (this.props.onConvertCurrencies) this.props.onConvertCurrencies([cur]);
		this.update({ expandedCurrency: null });
	};

	onConvertAllCurrencies = () => {
		const { foreignTransactions } = this.state;
		const currencies = Array.from(new Set((foreignTransactions || []).map(t => t.isoCurrencyCode).filter(Boolean)));
		if (currencies.length === 0) return;
		if (this.props.onConvertCurrencies) this.props.onConvertCurrencies(currencies);
		this.update({ expandedCurrency: null });
	};

	renderCurrencySummary() {
		let { foreignTransactions, expandedCurrency } = this.state;
		if (!foreignTransactions || foreignTransactions.length === 0) {
			return (
				<div className="dq-section">
					<h3>Foreign Currency Summary</h3>
					<p className="dq-empty">All transactions are in the account's base currency.</p>
				</div>
			);
		}

		// Group by currency
		const byCurrency = {};
		foreignTransactions.forEach(txn => {
			const cur = txn.isoCurrencyCode || "???";
			if (!byCurrency[cur]) {
				byCurrency[cur] = { count: 0, originalTotal: 0, cadTotal: 0, rates: [], transactions: [] };
			}
			byCurrency[cur].count += 1;
			byCurrency[cur].originalTotal += Math.abs(txn.originalAmount || 0);
			byCurrency[cur].cadTotal += Math.abs(txn.cadAmount || 0);
			if (txn.exchangeRate) byCurrency[cur].rates.push(txn.exchangeRate);
			byCurrency[cur].transactions.push(txn);
		});

		const totalCad = Object.values(byCurrency).reduce((sum, g) => sum + g.cadTotal, 0);

		return (
			<div className="dq-section" id="dq-currency">
				<div className="dq-section__header">
					<h3>Foreign Currency Summary</h3>
					<button className="dq-btn dq-btn--primary" onClick={this.onConvertAllCurrencies}>
						Convert All to CAD
					</button>
				</div>
				<p className="dq-currency-intro">
					{foreignTransactions.length} foreign currency transactions detected in this period
				</p>

				<table className="dq-currency-table">
					<thead>
						<tr>
							<th style={{ width: 24 }}></th>
							<th>Currency</th>
							<th>#Txns</th>
							<th>Original Total</th>
							<th>CAD Total</th>
							<th>Avg Rate</th>
							<th>Action</th>
						</tr>
					</thead>
					<tbody>
						{Object.entries(byCurrency).map(([cur, g]) => {
							const avgRate = g.rates.length > 0 ? g.rates.reduce((a, b) => a + b, 0) / g.rates.length : 0;
							const isExpanded = expandedCurrency === cur;
							return (
								<React.Fragment key={cur}>
									<tr
										className={`dq-currency-row ${isExpanded ? "dq-currency-row--expanded" : ""}`}
										onClick={() => this.onToggleCurrencyRow(cur)}
									>
										<td>
											{isExpanded ? <Icon.ChevronDown size={14} /> : <Icon.ChevronRight size={14} />}
										</td>
										<td><span className="dq-cur-tag">🌐 {cur}</span></td>
										<td>{g.count}</td>
										<td>{g.originalTotal.toFixed(2)} {cur}</td>
										<td>{this.formatCurrency(g.cadTotal)}</td>
										<td>{avgRate.toFixed(4)}</td>
										<td onClick={e => e.stopPropagation()}>
											<button className="dq-btn dq-btn--ghost" onClick={() => this.onConvertCurrency(cur)}>
												Convert to CAD
											</button>
										</td>
									</tr>
									{isExpanded && (
										<tr className="dq-currency-detail-row">
											<td colSpan={7}>
												<div className="dq-currency-detail">
													<table className="dq-currency-detail-table">
														<thead>
															<tr>
																<th>Date</th>
																<th>Description</th>
																<th>Original Amount</th>
																<th>CAD Amount</th>
																<th>Rate</th>
															</tr>
														</thead>
														<tbody>
															{g.transactions.map((txn, idx) => (
																<tr key={idx}>
																	<td>{txn.date}</td>
																	<td>{txn.name || txn.description}</td>
																	<td>{Math.abs(txn.originalAmount || 0).toFixed(2)} {cur}</td>
																	<td>{this.formatCurrency(Math.abs(txn.cadAmount || 0))}</td>
																	<td>{(txn.exchangeRate || 0).toFixed(4)}</td>
																</tr>
															))}
														</tbody>
													</table>
												</div>
											</td>
										</tr>
									)}
								</React.Fragment>
							);
						})}
					</tbody>
				</table>

				<div className="dq-currency-total">
					Total foreign currency exposure: <strong>{this.formatCurrency(totalCad)}</strong>
				</div>
			</div>
		);
	}

	render() {
		return (
			<div className="dq-panel">
				{this.renderSummaryCards()}
				{this.renderDuplicates()}
				{this.renderAlerts()}
				{this.renderCurrencySummary()}
			</div>
		);
	}
}

export default DataQualityPanel;
